import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import IconButton from "@cayuse/shared-react.components.icon-button";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { api } from "services";

import FeedTable from "components/FeedTable";
import SPLink from "components/SPLink";
import { PROPOSALS_BASE_ROUTE, COLUMN_HEADER_KEYS, PRIVILEGES } from "globals/constants";
import { useDictionary, useSelectedRows, useColumnHeaders, useActiveColumns, useHasPrivileges } from "hooks";
import useSimplePagination from "hooks/useSimplePagination";
import { findProposals } from "store/actions/proposals";
import { proposalListSelectors } from "store/selectors";
import { getKeysFromColumnHeaders, notifySuccess, spUrl } from "utils";
import history from "utils/history";

import styles from "./styles.css";


const ProjectProposalsView = ({ projectId }) => {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = useSimplePagination();
	const [ rows, setRows ] = useState([]);
	const [ busy, setBusy ] = useState();
	const getRowKey = useMemo(() => row => row._item.id, []);
	const { selectedRows, isSelectAll, setRowSelection } = useSelectedRows({ rows, getRowKey });
	const dictionary = useDictionary("proposals");
	const {
		[PRIVILEGES.PROPOSAL_CREATE]: canCreateProposals
	} = useHasPrivileges([ PRIVILEGES.PROPOSAL_CREATE ]);

	const { items, totalItems, isFetching: isLoading, isRefresh } = useSelector(proposalListSelectors.content);
	const { columnHeaders: formColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.PROPOSAL);
	const { columns: activeColumns } = useActiveColumns("PROPOSAL", formColumnHeaders);

	const hasItems = isNonEmptyArray(items);

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const onPageChange = pageNumber => setPageNumber(pageNumber);

	const onPageLimitChange = pageLimit => setPageLimit(pageLimit);

	useEffect(() => {
		const updatedRows = hasItems
			? items.map(item => {
				const isProposalNumberLinkable = isNonEmptyString(item.proposalId) && isNonEmptyString(item.proposalNumber);
				const linkedProposalNumber = isProposalNumberLinkable
					? <SPLink to={`${PROPOSALS_BASE_ROUTE}/${item.proposalId}`}>{item.proposalNumber}</SPLink>
					: "";

				return {
					_isSelected: selectedRows.has(item.id),
					_item: item,
					...item,
					proposalNumber: linkedProposalNumber
				};
			})
			: [];

		setRows(updatedRows);
	}, [ items, selectedRows, hasItems ]);

	useEffect(() => {
		if (projectId) {
			findProposals({
				pageLimit,
				pageOffset,
				searchTerms: { projectId },
				fieldKeys: requiredFieldKeys
			});
		}
	}, [
		pageLimit,
		pageOffset,
		projectId,
		requiredFieldKeys
	]);

	const addNew = async () => {
		setBusy(true);
		const response = await api.proposal.create({ projectId }, { notifyUponError: false });
		const proposal = response.submission;
		notifySuccess(dictionary.startNewProposal?.successMessages?.createProposal ?? "New proposal created successfully!");
		history.push(spUrl(`${PROPOSALS_BASE_ROUTE}/${proposal.proposalId}`));
		setBusy(false);
	};

	return (
		<>
			{canCreateProposals && <div className={styles.startNewButton}>
				<IconButton
					icon="plus"
					labelPosition="left"
					primary
					busy={busy}
					onClick={addNew}
				>
					{dictionary.addNew || "Add A Proposal"}
				</IconButton>
			</div>}
			<FeedTable isLoading={isLoading}
				isRefresh={isRefresh}
				headers={activeColumns}
				rows={rows}
				totalItems={totalItems}
				pageLimit={pageLimit}
				pageOffset={pageOffset}
				onPageChange={onPageChange}
				onPageLimitChange={onPageLimitChange}
				onRowSelectionChange={({ row, isSelected }) => setRowSelection(row, isSelected)}
				isSelectable
				isSelectAll={isSelectAll}
				accessibilityLabel={({ projectTitle, _item }) => (
					`${projectTitle}, ${dictionary.accessibilityLabelProposalNumber || "Proposal #"}${_item.proposalNumber}`
				)}
				noResultsLabel={dictionary.noResults || "No Saved Proposals"}
				captionText="Project Proposals"
			/>
		</>
	);
};

export default ProjectProposalsView;
